import { createI18n } from 'vue-i18n'
import en from './en.json'
import ar from './ar.json'
import {
	DEFAULT_LANGUAGE,
	LANGUAGE_DIRECTIONS,
	LANG_STORAGE_KEY,
	SUPPORTED_LANGUAGES
} from '@/helpers/constants'

const i18n = createI18n({
	legacy: false,
	globalInjection: true,
	locale: DEFAULT_LANGUAGE,
	fallbackLocale: DEFAULT_LANGUAGE,
	// the profile copy carries a few intentional <mark> highlights
	warnHtmlMessage: false,
	messages: { en, ar }
})

const isSupported = (lang) => SUPPORTED_LANGUAGES.includes(lang)

/** Reading direction of a language. */
export function getLanguageDirection(lang) {
	return LANGUAGE_DIRECTIONS[lang] || 'ltr'
}

/** localStorage > browser language > default. */
export function getStoredLanguage() {
	try {
		const stored = localStorage.getItem(LANG_STORAGE_KEY)
		if (isSupported(stored)) return stored
	} catch (error) {
		// storage blocked (private mode) — fall back to the browser locale
	}

	const browser = (navigator.language || DEFAULT_LANGUAGE).toLowerCase()
	return browser.startsWith('ar') ? 'ar' : DEFAULT_LANGUAGE
}

/**
 * Single place that switches the language: locale, persistence, the
 * <html lang/dir> attributes and the document title all move together.
 */
export function applyLanguage(lang) {
	const value = isSupported(lang) ? lang : DEFAULT_LANGUAGE

	i18n.global.locale.value = value

	const html = document.documentElement
	html.setAttribute('lang', value)
	html.setAttribute('dir', getLanguageDirection(value))
	document.title = i18n.global.t('general.document-title')

	try {
		localStorage.setItem(LANG_STORAGE_KEY, value)
	} catch (error) {
		// nothing to persist to — the language still applies for this visit
	}

	return value
}

/** Switch to a specific language. */
export function changeLanguage(lang) {
	return applyLanguage(lang)
}

/** Flip between English and Arabic. */
export function toggleLanguage() {
	return applyLanguage(i18n.global.locale.value === 'ar' ? 'en' : 'ar')
}

export default i18n
